import * as React from 'react';
import { useState } from 'react';
import * as Progress from '@radix-ui/react-progress';
import { InfoIcon } from '@phosphor-icons/react';
import { MeteringFromTable, MeteringFromApi } from '../../app/api/protected/metering/route';
import { Popover, PopoverTrigger, PopoverContent } from "@heroui/popover";
const valueColors = ["bg-gray-100", "bg-gray-200", "bg-gray-300", "bg-gray-400", "bg-gray-500", "bg-gray-600", "bg-gray-700", "bg-gray-800"];
const temperatureColors = [{
  value: 50,
  color: "bg-green-500"
}, {
  value: 75,
  color: "bg-yellow-500"
}, {
  value: 100,
  color: "bg-red-500"
}];
const fileTypesMap: {
  [key: string]: string;
} = {
  "image": "Images",
  "video": "Videos",
  "audio": "Audio",
  "pdfs": "PDFs",
  "documents": "Documents",
  "other": "Other"
};
const mapFileTypesToMeter = (metering: MeteringFromApi) => {
  const meterValues: MeteringFromApi = {
    count: metering.count,
    size: metering.size,
    categories: {} as {
      [key: string]: any;
    }
  };
  Object.keys(metering.categories).forEach(category => {
    meterValues.categories[fileTypesMap[category]] = metering.categories[category];
  });
  return meterValues;
};
interface MeterProps {
  minimal?: boolean;
  overview?: boolean; // if true, don't show the breakdown or the tooltip
  title: string;
  values: MeteringFromTable;
  total?: number;
  units?: string;
  breakdownTooltip?: boolean;
  breakdownLink?: string;
  breakdownLegend?: boolean;
  breakdownDropdown?: boolean;
  temperature?: boolean;
}
const MeterBreakdown = (props: MeterProps) => {
  const {
    values,
    units
  } = props;
  const maxInBytes = (values as MeteringFromTable).quotaInBytes ? (values as MeteringFromTable).quotaInBytes : 0;
  const meterValues = (values as MeteringFromTable).usageInBytes ? [] : mapFileTypesToMeter(values as unknown as MeteringFromApi);
  const valuesArray = (values as MeteringFromTable).usageInBytes ? [] : Object.values((meterValues as MeteringFromApi).categories).map((category: any) => category.size);
  const keysArray = (values as MeteringFromTable).usageInBytes ? [] : Object.keys((meterValues as MeteringFromApi).categories);
  const total = units && maxInBytes ? valuesArray.reduce((acc, curr) => acc + curr / maxInBytes * 100, 0) : valuesArray.reduce((acc, curr) => acc + curr, 0);
  return <div className="box-border flex flex-row items-center justify-center min-w-[150px] w-[100%] h-[100%] bg-gray-200 rounded-full" data-sentry-component="MeterBreakdown" data-sentry-source-file="meter.tsx">
      <Progress.Root value={total} className="relative w-[calc(100%-2px)] h-[calc(100%-2px)] bg-gray-800 rounded-full overflow-hidden" data-sentry-element="unknown" data-sentry-source-file="meter.tsx">
        {valuesArray.map((value, index) => <Progress.Indicator key={keysArray[index]} className={`absolute top-0 h-full ${valueColors[index]}`} style={{
        left: `${units && maxInBytes ? Math.round(valuesArray.filter((_, i) => i < index).reduce((acc, curr) => acc + curr / maxInBytes * 100, 0)) : Math.round(valuesArray.filter((_, i) => i < index).reduce((acc, curr) => acc + curr, 0))}%`,
        width: `${units && maxInBytes ? Math.round(value / maxInBytes * 100) : Math.round(value)}%`
      }} />)}
      </Progress.Root>
    </div>;
};
interface MeterBreakdownAndInfoProps extends MeterProps {}
const MeterBreakdownAndInfo = (props: MeterBreakdownAndInfoProps) => {
  const {
    values,
    units,
    title,
    breakdownLegend,
    breakdownLink
  } = props;
  const maxInBytes = (values as MeteringFromTable).quotaInBytes ? (values as MeteringFromTable).quotaInBytes : 0;
  const meterValues = (values as MeteringFromTable).usageInBytes ? [] : mapFileTypesToMeter(values as unknown as MeteringFromApi);
  const valuesArray = (values as MeteringFromTable).usageInBytes ? [] : Object.values((meterValues as MeteringFromApi).categories).map((category: any) => category.size);
  const keysArray = (values as MeteringFromTable).usageInBytes ? [] : Object.keys((meterValues as MeteringFromApi).categories);
  const total = valuesArray.reduce((acc, curr) => acc + curr, 0);
  const maxInGb = maxInBytes && units === 'GB' ? Math.round(maxInBytes / 1073741824) : maxInBytes;
  const totalInGb = maxInBytes && units === 'GB' ? Math.round(total / 1073741824) : total;
  return <div className="flex flex-col gap-2 w-[200px]" data-sentry-component="MeterBreakdownAndInfo" data-sentry-source-file="meter.tsx">
      <div className="flex flex-row items-center justify-center gap-1 text-[18px]">
        <InfoIcon size={20} data-sentry-element="InfoIcon" data-sentry-source-file="meter.tsx" />
        <div className="text-[18px]">{totalInGb}{units ? ` of ${maxInGb} ${units}` : '%'} Used</div>
      </div>
      <hr className="w-[96%] h-[1px] bg-gray-200" />
      <div className="flex flex-row items-center justify-center w-full h-[30px]">
        <MeterBreakdown total={total} title={title} values={values} units={units} breakdownLegend={breakdownLegend} data-sentry-element="MeterBreakdown" data-sentry-source-file="meter.tsx" />
      </div>
      {breakdownLegend && <div className="flex flex-row flex-wrap items-center justify-center gap-2 text-[12px] text-gray-200">
        {keysArray.map((key, index) => <div className="flex flex-row items-center justify-center gap-1" key={key}>
            <div className={`w-[10px] h-[10px] rounded-full ${valueColors[index]}`}></div>
            <div>{key}</div>
          </div>)}
      </div>}
      <div className="flex flex-row justify-center mx-2">
        Upgrade your account to increase your upload capacity.
      </div>
      {breakdownLink && <button role="link" className="cursor-pointer flex flex-row items-center justify-center w-full h-[30px] bg-gray-500 rounded-md" onClick={() => window.open(breakdownLink, '_blank')}>
          Learn More
        </button>}
    </div>;
};
const Meter = (props: MeterProps) => {
  const {
    overview,
    title,
    values,
    units,
    breakdownLink,
    breakdownTooltip,
    breakdownLegend,
    breakdownDropdown,
    minimal,
    temperature
  } = props;
  const maxInBytes = values && (values as MeteringFromTable).quotaInBytes ? (values as MeteringFromTable).quotaInBytes : 0;
  const meterValues = values && (values as MeteringFromTable).usageInBytes ? [] : mapFileTypesToMeter(values as unknown as MeteringFromApi);
  const valuesArray = values && (values as MeteringFromTable).usageInBytes ? [] : Object.values((meterValues as MeteringFromApi).categories).map((category: any) => category.size);
  const percentUsed: number = (values as MeteringFromTable).usageInBytes ? (values as MeteringFromTable).usageInBytes / maxInBytes * 100 : Number((units && maxInBytes ? valuesArray.reduce((acc, curr) => acc + curr / maxInBytes * 100, 0) : valuesArray.reduce((acc, curr) => acc + curr, 0)).toFixed(1));
  const total = valuesArray.reduce((acc, curr) => acc + curr, 0);
  // Bar width and label use the same percent so the fill matches the number (e.g. "50%" != 49.3% width).
  const barWidthPercent = percentUsed < 1 ? Math.max(0, percentUsed) : Math.round(percentUsed);
  const percentLabel = percentUsed < 1 ? '< 1%' : `${barWidthPercent}%`;
  const progressMax = Math.max(100, Math.ceil(barWidthPercent));
  const pillMinWidth = `max(${minimal ? 50 : 120}px, calc(${percentLabel.length}ch + ${minimal ? 12 : 20}px))`;
  const temperatureColor = temperatureColors.find(color => percentUsed <= color.value) || temperatureColors[temperatureColors.length - 1];
  const [isHovered, setIsHovered] = useState(false);
  const getStatusInfo = () => {
    let header, message, color;
    if (percentUsed <= 50) {
      header = "Good";
      message = "You have plenty of usage capacity available.";
      color = "green";
    } else if (percentUsed < 75) {
      header = "Moderate";
      message = "You've used more than half of your usage capacity.";
      color = "yellow";
    } else if (percentUsed < 100) {
      header = "Warning";
      message = "You're running low on usage capacity.";
      color = "red";
    } else {
      header = "Critical";
      message = "You've reached your usage capacity limit.";
      color = "red";
    }
    return {
      header,
      message,
      colors: {
        header: `text-${color}-500`,
        indicator: !temperature || !isHovered ? "bg-gray-500" : `bg-${color}-500`
      }
    };
  };
  const statusInfo = getStatusInfo();
  let canUseBreakdown = true;
  if ((values as MeteringFromTable).usageInBytes) {
    canUseBreakdown = false;
  }
  return <div className={`z-[1100] flex opacity-70 hover:opacity-100 cursor-pointer ${minimal ? 'gap-2 flex-row' : 'flex-row'} justify-center items-center`} data-sentry-component="Meter" data-sentry-source-file="meter.tsx">
      {title && <label htmlFor={title} className={`whitespace-nowrap ${minimal ? 'text-sm' : 'text-sm'}`}>{title}:</label>}
      {overview ? <Popover showArrow={true} placement="bottom-end" classNames={{
      content: 'flex flex-col gap-3 w-[200px] min-w-[250px] text-black bg-white p-4 rounded-md'
    }}>
          <PopoverTrigger asChild>
            <button type="button" aria-label={`${title || 'Progress'}: ${percentLabel} used`} aria-haspopup="dialog" className="bg-transparent border-none p-0 cursor-pointer max-w-full" onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}>
              <Progress.Root value={barWidthPercent} max={progressMax} style={{
            minWidth: pillMinWidth
          }} className={`relative flex w-max max-w-full box-border ${minimal ? 'h-[26px] min-h-[24px] bg-gray-600' : 'min-h-[24px] h-full bg-gray-200'} z-[1200] rounded-full overflow-hidden`}>
                <Progress.Indicator className={`absolute left-0 top-0 h-full transition-colors duration-200 rounded-r-md shadow-md ${statusInfo.colors.indicator}`} style={{
              width: `${barWidthPercent}%`
            }} />
                <div className={`relative z-[1300] box-border flex min-h-0 w-full min-w-0 flex-1 items-center justify-center self-stretch px-1.5 ${minimal ? '' : 'gap-1'} text-[14px] tabular-nums leading-none whitespace-nowrap text-gray-800`}>
                  <span className="text-center text-white">{percentLabel}</span>
                </div>
              </Progress.Root>
            </button>
          </PopoverTrigger>
          <PopoverContent>
            <>
              {statusInfo.header && <h2 className="text-center text-gray-900 m-0 p-0 font-normal leading-tight">
                  Capacity Status:{" "}
                  <span className={`${statusInfo.colors.header} font-semibold`}>{statusInfo.header}</span>
                </h2>}
              {statusInfo.message && <div className="flex flex-row justify-center text-center mx-2">
                  {statusInfo.message}
                </div>}
              <button role="link" className="cursor-pointer text-white flex flex-row items-center justify-center w-full h-[30px] bg-blue-500 rounded-md hover:bg-blue-600" onClick={() => window.location.href = 'mailto:csm@tranquility.ai?subject=Upgrade%20Inquiry&body=I%20would%20like%20to%20learn%20more%20about%20your%20the%20upgrade%20options.'}>
                Email us to learn more
              </button>
            </>
          </PopoverContent>
        </Popover> : <MeterBreakdown total={total} title={title} values={values} units={units} breakdownLegend={breakdownLegend} />}
    </div>;
};
Meter.displayName = 'Meter';
export default Meter;